import { createFileRoute } from '@tanstack/react-router'
import { icons as lucideIcons } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { PointOfSaleProductGroup } from '@/api/admin-products-types'
import { useUpdateProductGroupsForPointOfSale } from '@/api/api-mutations'
import type { Asset } from '@/api/common-types'
import { useListProductGroups } from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Button } from '@/components/base/button'
import { Combobox } from '@/components/base/combobox'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { Switch } from '@/components/base/switch'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'
import { Photo } from '@/icons/photo'
import { Trash } from '@/icons/trash'
import { useDebounce } from '@/utils/hooks'

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/$pointOfSaleUuid/product-menu')({
	component: PointOfSaleProductGroups
})

type GroupRow = {
	productGroupUuid: string
	name: string
	icon: string
	isDefault: boolean
	image?: Asset
}

const kebabToPascal = (value: string) =>
	value
		.split('-')
		.map(part => part.charAt(0).toUpperCase() + part.slice(1))
		.join('')

function LucideGlyph({ name, className }: { name: string; className?: string }) {
	const Glyph = lucideIcons[kebabToPascal(name) as keyof typeof lucideIcons]
	if (!Glyph) {
		return null
	}
	return <Glyph className={className} />
}

function PointOfSaleProductGroups() {
	const { pointOfSaleUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api =>
				await api.listProductGroupsForPointOfSale({
					path: { pointOfSaleUuid },
					query: { limit: 100, offset: 0 }
				})
			}
		>
			{data => <PointOfSaleProductGroupsComponent initial={data.groups} />}
		</RouteModal>
	)
}

function PointOfSaleProductGroupsComponent({ initial }: { initial: PointOfSaleProductGroup[] }) {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()

	const [search, setSearch] = useState('')
	const debouncedSearch = useDebounce(search)
	const availableGroups = useListProductGroups({ q: debouncedSearch, limit: 50 })

	const [pickerKey, setPickerKey] = useState(0)

	const updateGroups = useUpdateProductGroupsForPointOfSale()

	const iconOptions = useMemo(() => {
		const Icons = L.Settings.PointOfSale.ProductMenu.Icons
		return [
			{ value: '', label: L.Settings.PointOfSale.ProductMenu.IconNone() },
			{ value: 'shopping-bag', label: Icons.ShoppingBag() },
			{ value: 'shopping-cart', label: Icons.ShoppingCart() },
			{ value: 'coffee', label: Icons.Coffee() },
			{ value: 'cup-soda', label: Icons.CupSoda() },
			{ value: 'utensils', label: Icons.Utensils() },
			{ value: 'pizza', label: Icons.Pizza() },
			{ value: 'sandwich', label: Icons.Sandwich() },
			{ value: 'ice-cream-cone', label: Icons.IceCream() },
			{ value: 'beer', label: Icons.Beer() },
			{ value: 'wine', label: Icons.Wine() },
			{ value: 'croissant', label: Icons.Bakery() },
			{ value: 'gift', label: Icons.Gift() }
		]
	}, [L])

	const form = useAppForm({
		defaultValues: {
			groups: initial.map<GroupRow>(group => ({
				productGroupUuid: group.uuid,
				name: group.name,
				icon: group.icon ?? '',
				isDefault: group.isDefault ?? false,
				image: group.image
			}))
		},
		onSubmit: async ({ value }) => {
			const result = await updateGroups({
				path: { pointOfSaleUuid },
				body: {
					groups: value.groups.map(group => ({
						productGroupUuid: group.productGroupUuid,
						icon: group.icon || undefined,
						isDefault: group.isDefault
					}))
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.PointOfSale.ProductMenu.SaveSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.PointOfSale.ProductMenu.EditHeader()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<Text className='text-sm text-ui-fg-subtle'>
						{L.Settings.PointOfSale.ProductMenu.EditDescription()}
					</Text>

					<form.Field name='groups' mode='array'>
						{arrayField => {
							const rows = arrayField.state.value
							const selectedUuids = new Set(rows.map(row => row.productGroupUuid))
							const options =
								availableGroups.data?.groups
									.filter(group => !selectedUuids.has(group.uuid))
									.map(group => ({ label: group.name, value: group.uuid })) ?? []

							return (
								<div className='flex flex-col gap-y-4'>
									<Combobox
										key={pickerKey}
										options={options}
										optionsLoading={availableGroups.isLoading}
										onSearchValueChange={setSearch}
										placeholder={L.Settings.PointOfSale.ProductMenu.AddPlaceholder()}
										onChange={value => {
											if (!value || typeof value !== 'string') {
												return
											}
											const group = availableGroups.data?.groups.find(g => g.uuid === value)
											arrayField.pushValue({
												productGroupUuid: value,
												name: group?.name ?? '',
												icon: '',
												isDefault: false,
												image: group?.image
											})
											setSearch('')
											setPickerKey(key => key + 1)
										}}
									/>

									{rows.length === 0 ? (
										<div className='flex items-center justify-center rounded-lg border border-dashed border-ui-border-base py-8'>
											<Text size='small' className='text-ui-fg-subtle'>
												{L.Settings.PointOfSale.ProductMenu.NoGroupsSelected()}
											</Text>
										</div>
									) : (
										<div className='flex flex-col gap-y-3'>
											{rows.map((row, index) => (
												<div
													key={row.productGroupUuid}
													className='flex flex-col gap-y-3 rounded-lg border border-ui-border-base bg-ui-bg-base p-3'
												>
													<div className='flex items-center gap-x-3'>
														<div className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-ui-border-base bg-ui-bg-component'>
															{row.image ? (
																<AssetImage
																	asset={row.image}
																	className='size-full'
																	preferredVariant='THUMBNAIL'
																/>
															) : row.icon ? (
																<LucideGlyph
																	name={row.icon}
																	className='size-5 text-ui-fg-subtle'
																/>
															) : (
																<Photo className='text-ui-fg-muted' />
															)}
														</div>
														<Text className='flex-1 truncate font-medium text-ui-fg-base'>
															{row.name}
														</Text>
														<IconButton
															type='button'
															variant='transparent'
															size='small'
															tooltipText={L.General.Delete()}
															onClick={() => arrayField.removeValue(index)}
														>
															<Trash />
														</IconButton>
													</div>

													<div className='flex flex-col gap-y-3 border-t border-ui-border-base pt-3 sm:flex-row sm:items-center sm:justify-between'>
														<div className='flex flex-col gap-y-1'>
															<Text
																size='small'
																weight='plus'
																className='text-ui-fg-subtle'
															>
																{L.Settings.PointOfSale.ProductMenu.IconLabel()}
															</Text>
															{row.isDefault ? (
																<Text
																	size='small'
																	className='max-w-[224px] text-ui-fg-muted'
																>
																	{L.Settings.PointOfSale.ProductMenu.IconNotUsedWhenIndividual()}
																</Text>
															) : row.image ? (
																<Text
																	size='small'
																	className='max-w-[224px] text-ui-fg-muted'
																>
																	{L.Settings.PointOfSale.ProductMenu.IconUsesImageHint()}
																</Text>
															) : (
																<form.Field name={`groups[${index}].icon`}>
																	{iconField => (
																		<div className='w-56'>
																			<Combobox
																				options={iconOptions}
																				value={iconField.state.value}
																				placeholder={L.Settings.PointOfSale.ProductMenu.IconPlaceholder()}
																				onChange={value =>
																					iconField.handleChange(
																						typeof value === 'string'
																							? value
																							: ''
																					)
																				}
																			/>
																		</div>
																	)}
																</form.Field>
															)}
														</div>

														<div className='flex items-start gap-x-3'>
															<div className='flex flex-col gap-y-1'>
																<Text
																	size='small'
																	weight='plus'
																	className='text-ui-fg-subtle'
																>
																	{L.Settings.PointOfSale.ProductMenu.ShowIndividually()}
																</Text>
																<Text
																	size='small'
																	className='max-w-[220px] text-ui-fg-muted'
																>
																	{L.Settings.PointOfSale.ProductMenu.ShowIndividuallyHint()}
																</Text>
															</div>
															<form.Field name={`groups[${index}].isDefault`}>
																{defaultField => (
																	<Switch
																		checked={defaultField.state.value}
																		onCheckedChange={checked =>
																			defaultField.handleChange(checked)
																		}
																	/>
																)}
															</form.Field>
														</div>
													</div>
												</div>
											))}
										</div>
									)}
								</div>
							)
						}}
					</form.Field>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
